/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import { useState, useEffect, useRef, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import MDInput from "components/MDInput";
import axios from "axios";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import swal from "sweetalert";
import MDDatePicker from "components/MDDatePicker";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CircularProgress from "@mui/material/CircularProgress";
import StraightenIcon from "@mui/icons-material/Straighten";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDDropzone from "components/MDDropzone";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import checked from "assets/images/checked.png";
import unchecked from "assets/images/unchecked.png";
import { API_HOHOLOK, HOHOLOK_UPLOADS } from "utils";

// Data

// Fungsi format rupiah
function formatRupiah(angka) {
  if (!angka) return "";
  const numberString = angka.replace(/[^\d]/g, "");
  const sisa = numberString.length % 3;
  let rupiah = numberString.substr(0, sisa);
  const ribuan = numberString.substr(sisa).match(/\d{3}/g);
  if (ribuan) {
    rupiah += (sisa ? "." : "") + ribuan.join(".");
  }
  return rupiah ? "Rp " + rupiah : "";
}

function Verifikasi_Progres() {
  const [proyekList, setProyekList] = useState([]);
  const [selectedProyek, setSelectedProyek] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openInputModal, setOpenInputModal] = useState(false);

  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [inputData, setInputData] = useState({
    realisasi: "",
    tenaga_kerja: "",
    catatan: "",
    dokumentasi: [],
    satuan: "",
    idx: null,
    nama_sub_pekerjaan: "",
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [idUser, setIdUser] = useState("");

  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [id_perusahaan, setIdPerusahaan] = useState(null);
  const [status, setStatus] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  // State untuk koordinat lokasi
  const [currentLocation, setCurrentLocation] = useState({
    latitude: null,
    longitude: null,
    accuracy: null,
    timestamp: null,
    address: null,
  });
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [distanceFromProject, setDistanceFromProject] = useState(null);
  const [isAtProjectLocation, setIsAtProjectLocation] = useState(false);
  const [showLocationWarningDialog, setShowLocationWarningDialog] =
    useState(false);

  // Radius proyek dalam KM (radiusInput datang dalam meter; default 250m)
  const projectRadiusKm = useMemo(() => {
    const radiusMeter = Number(
      selectedProyek && selectedProyek.radiusInput
        ? selectedProyek.radiusInput
        : 250
    );
    return radiusMeter / 1000;
  }, [selectedProyek?.radiusInput]);

  // Label kedekatan lokasi untuk tampilan UI
  const proximityLabel = useMemo(() => {
    if (typeof distanceFromProject !== "number") return null;
    if (distanceFromProject <= projectRadiusKm) return " (Dalam area proyek)";
    if (distanceFromProject <= projectRadiusKm * 4) return " (Dekat proyek)";
    return " (Jauh dari proyek)";
  }, [distanceFromProject, projectRadiusKm]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  // Fungsi untuk mendapatkan lokasi perangkat
  const getCurrentLocation = () => {
    setLocationLoading(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Geolocation tidak didukung oleh browser ini");
      setLocationLoading(false);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 60000,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const timestamp = new Date(position.timestamp);

        const newLocation = {
          latitude,
          longitude,
          accuracy,
          timestamp,
          address: null,
        };

        setCurrentLocation(newLocation);
        setLocationLoading(false);

        // Opsional: Dapatkan alamat dari koordinat
        getAddressFromCoordinates(latitude, longitude);

        // Hitung jarak dari proyek setelah mendapatkan lokasi
        if (
          selectedProyek &&
          selectedProyek.latitude &&
          selectedProyek.longitude
        ) {
          setTimeout(() => {
            calculateDistanceFromProject();
          }, 700);
        }
      },
      (error) => {
        let errorMessage = "Tidak dapat mendapatkan lokasi";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Izin lokasi ditolak oleh pengguna";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Informasi lokasi tidak tersedia";
            break;
          case error.TIMEOUT:
            errorMessage = "Waktu permintaan lokasi habis";
            break;
          default:
            errorMessage = "Terjadi kesalahan yang tidak diketahui";
            break;
        }

        setLocationError(errorMessage);
        setLocationLoading(false);
      },
      options
    );
  };

  // Fungsi untuk mendapatkan alamat dari koordinat (opsional)
  const getAddressFromCoordinates = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
      );

      if (response.ok) {
        const data = await response.json();
        setCurrentLocation((prev) => ({
          ...prev,
          address: data.display_name,
        }));
      }
    } catch (error) {
      console.log("Tidak dapat mendapatkan alamat:", error);
    }
  };

  // Fungsi untuk menyalin koordinat ke clipboard
  const copyCoordinatesToClipboard = () => {
    if (currentLocation.latitude && currentLocation.longitude) {
      const coordinates = `${currentLocation.latitude}, ${currentLocation.longitude}`;
      navigator.clipboard
        .writeText(coordinates)
        .then(() => {
          swal(
            "Berhasil",
            "Koordinat berhasil disalin ke clipboard",
            "success"
          );
        })
        .catch(() => {
          swal("Gagal", "Tidak dapat menyalin koordinat", "error");
        });
    }
  };

  // Fungsi untuk menghitung jarak antara dua koordinat (formula Haversine)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius bumi dalam kilometer
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Jarak dalam kilometer
    return distance;
  };

  // Fungsi untuk menghitung jarak dari lokasi proyek
  const calculateDistanceFromProject = () => {
    if (
      currentLocation.latitude &&
      currentLocation.longitude &&
      selectedProyek &&
      selectedProyek.latitude &&
      selectedProyek.longitude
    ) {
      const distance = calculateDistance(
        currentLocation.latitude,
        currentLocation.longitude,
        selectedProyek.latitude,
        selectedProyek.longitude
      );

      setDistanceFromProject(distance);
    } else {
      setDistanceFromProject(null);
    }
  };

  // Update jarak ketika lokasi atau proyek berubah
  useEffect(() => {
    if (
      currentLocation.latitude &&
      currentLocation.longitude &&
      selectedProyek &&
      selectedProyek.latitude &&
      selectedProyek.longitude
    ) {
      calculateDistanceFromProject();
    } else {
      // Reset jika data tidak lengkap
      setDistanceFromProject(null);
      setIsAtProjectLocation(false);
    }
  }, [
    currentLocation.latitude,
    currentLocation.longitude,
    selectedProyek?.latitude,
    selectedProyek?.longitude,
  ]);

  // Update status lokasi proyek ketika jarak berubah
  useEffect(() => {
    if (distanceFromProject !== null) {
      // Bandingkan terhadap projectRadiusKm (keduanya dalam km)
      setIsAtProjectLocation(distanceFromProject <= projectRadiusKm);
    } else {
      setIsAtProjectLocation(false);
    }
  }, [distanceFromProject, projectRadiusKm]);

  // Reset status lokasi ketika proyek berubah
  useEffect(() => {
    setIsAtProjectLocation(false);
    setDistanceFromProject(null);
  }, [selectedProyek]);

  // List satuan umum konstruksi

  const [tableRows, setTableRows] = useState([]);
  const [tableColumns] = useState([
    { Header: "Uraian Pekerjaan", accessor: "uraian_pekerjaan", width: "30%" },
    { Header: "Volume", accessor: "volume", width: "10%" },
    { Header: "Satuan", accessor: "satuan", width: "10%" },

    { Header: "Realisasi", accessor: "realisasi", width: "10%" },
    { Header: "Jumlah Tenaga Kerja", accessor: "tenaga_kerja", width: "10%" },
    { Header: "Catatan", accessor: "catatan", width: "15%" },
    { Header: "Dokumentasi", accessor: "dokumentasi", width: "15%" },
    { Header: "Verifikasi", accessor: "verifikasi", width: "8%" },
    { Header: "Aksi", accessor: "aksi", width: "10%" },
  ]);

  // Handler untuk MDDropzone
  const dropzoneRef = useRef(null);
  // Inject file ke Dropzone saat mount jika dokumentasi tidak null
  useEffect(() => {
    if (
      dropzoneRef.current &&
      inputData.dokumentasi &&
      Array.isArray(inputData.dokumentasi) &&
      inputData.dokumentasi.length > 0
    ) {
      const dz = dropzoneRef.current.dropzone;
      // Hanya inject jika Dropzone kosong
      if (dz && dz.files.length === 0) {
        inputData.dokumentasi.forEach((file) => {
          // Hindari duplikasi file
          if (
            !dz.files.find((f) => f.name === file.name && f.size === file.size)
          ) {
            dz.emit("addedfile", file);
            dz.emit("complete", file);
          }
        });
      }
    }
  }, [inputData.dokumentasi]);

  const handleDropzoneChange = (files) => {
    setInputData((prev) => ({
      ...prev,
      dokumentasi: files,
      nama_sub_pekerjaan: inputData.nama_sub_pekerjaan,
    }));
  };

  // Preview file yang sudah diupload (tabel)
  const renderPreview = () => {
    if (inputData.dokumentasi && inputData.dokumentasi.length > 0) {
      return (
        <div style={{ marginBottom: 8 }}>
          <MDTypography variant="caption" color="text" mb={1}>
            File terupload:
          </MDTypography>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: 4,
              fontSize: 13,
              fontFamily: "inherit, Arial, sans-serif",
            }}
          >
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 6,
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  No
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 6,
                    textAlign: "left",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Nama File
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 6,
                    textAlign: "right",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Ukuran
                </th>
              </tr>
            </thead>
            <tbody>
              {inputData.dokumentasi.map((file, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: 6,
                      textAlign: "center",
                      fontWeight: 400,
                      fontSize: 12,
                    }}
                  >
                    {idx + 1}
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: 6,
                      fontWeight: 400,
                      fontSize: 12,
                    }}
                  >
                    {file.name}
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: 6,
                      textAlign: "right",
                      fontWeight: 400,
                      fontSize: 12,
                    }}
                  >
                    {(file.size / 1024).toFixed(2)} KB
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  // Helper format tanggal Indonesia
  function formatTanggalIndo(dateStr) {
    if (!dateStr) return "-";
    const bulan = [
      "Januari",
      "Februari",
      "Maret",
      "April",
      "Mei",
      "Juni",
      "Juli",
      "Agustus",
      "September",
      "Oktober",
      "November",
      "Desember",
    ];
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    return `${d.getDate()} ${bulan[d.getMonth()]} ${d.getFullYear()}`;
  }

  useEffect(() => {
    const userStorage = localStorage.getItem("user");
    if (userStorage) {
      const user = JSON.parse(localStorage.getItem("user"));
      const statusKU = user.status;
      if (statusKU == "user") {
        setIdPerusahaan(user.id_perusahaan);
      }
      setStatus(statusKU);
      setIdUser(user.uid);
    }
  }, []);

  // Get current location when component mounts
  useEffect(() => {
    getCurrentLocation();
  }, []);

  useEffect(() => {
    if (tahun && status) {
      fetchProyek();
    }
  }, [tahun, status]);

  const fetchProyek = () => {
    setLoading(true);
    const url =
      status == "user"
        ? `${API_HOHOLOK}proyek/perusahaan/${id_perusahaan}/tahun/${tahun}`
        : `${API_HOHOLOK}proyek/tahun/${tahun}`;
    axios
      .get(url)
      .then((res) => {
        const data = res.data || [];
        const dataKU = data.map((item) => ({
          label: item.nama_proyek,
          value: item.id_proyek,
          ...item,
        }));
        setProyekList(dataKU);
        setSelectedProyek(dataKU[0]);
        setLoading(false);
      })
      .catch(() => {
        setProyekList([]);
        setLoading(false);
      });
  };

  // Fungsi untuk fetch pekerjaan utama & sub pekerjaan
  const fetchPekerjaanProyek = (idProyek) => {
    // Ambil tanggal hari ini format YYYY-MM-DD
    const today = new Date(selectedDate);
    console.log(today);
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}`;
    axios
      .get(
        `${API_HOHOLOK}proyek/${idProyek}/pekerjaan-laporan-harian?tanggal=${todayStr}`
      )
      .then((res) => {
        const pekerjaanUtama = res.data?.pekerjaan_utama || [];
        // Hitung total harga semua sub pekerjaan untuk bobot global
        let allSubs = pekerjaanUtama.flatMap((utama) =>
          (utama.sub_pekerjaan || []).map((sub) => {
            const hargaSatuan = Number(sub.harga_satuan) || 0;
            const volume = Number(sub.volume) || 0;
            const totalHarga = hargaSatuan * volume;
            return {
              id_pekerjaan_utama: utama.id_pekerjaan_utama,
              nama_pekerjaan: utama.nama_pekerjaan,
              uraian_pekerjaan: sub.uraian_pekerjaan,
              volume: sub.volume,
              satuan: sub.satuan,
              harga_satuan: hargaSatuan,
              total_harga: totalHarga,
              laporan_harian: sub.laporan_harian || null,
            };
          })
        );
        const totalAll = allSubs.reduce(
          (sum, s) => sum + (s.total_harga || 0),
          0
        );
        // Hitung total harga & bobot per pekerjaan utama
        const rows = [];
        pekerjaanUtama.forEach((utama) => {
          // Hitung total harga & bobot pekerjaan utama
          const subs = (utama.sub_pekerjaan || []).map((sub, idx) => {
            const hargaSatuan = Number(sub.harga_satuan) || 0;
            const volume = Number(sub.volume) || 0;
            const totalHarga = hargaSatuan * volume;
            // Hitung bobot sub pekerjaan terhadap totalAll
            const bobot =
              totalAll > 0
                ? ((totalHarga / totalAll) * 100).toFixed(2)
                : "0.00";
            // Data laporan harian
            const laporan = sub.laporan_harian;
            // Untuk catatan panjang, gunakan tooltip dan potong jika lebih dari 30 karakter
            let catatanCell = "-";
            if (laporan && laporan.catatan) {
              catatanCell =
                laporan.catatan.length > 30 ? (
                  <Tooltip title={laporan.catatan} placement="top" arrow>
                    <span
                      style={{
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                        maxWidth: 120,
                      }}
                    >
                      {laporan.catatan.slice(0, 30)}...
                    </span>
                  </Tooltip>
                ) : (
                  <span>{laporan.catatan}</span>
                );
            }
            // Untuk dokumentasi, tampilkan preview foto/video jika ada
            let dokumentasiCell = "-";
            if (
              laporan &&
              laporan.dokumentasi_foto &&
              laporan.dokumentasi_foto !== "[]"
            ) {
              try {
                const files = JSON.parse(laporan.dokumentasi_foto);
                dokumentasiCell = (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 4,
                      position: "relative",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setPreviewFiles(files);
                      setOpenPreviewDialog(true);
                    }}
                    title={
                      files.length > 2
                        ? `Klik untuk lihat semua (${files.length}) file`
                        : "Klik untuk preview"
                    }
                  >
                    {files.slice(0, 2).map((url, i) => {
                      const fullUrl = url.startsWith("http")
                        ? url
                        : `${HOHOLOK_UPLOADS}${url}`;
                      const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(
                        fullUrl
                      );
                      const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(fullUrl);
                      if (isImage) {
                        return (
                          <img
                            key={i}
                            src={fullUrl}
                            alt="dokumentasi"
                            style={{
                              maxWidth: 40,
                              maxHeight: 30,
                              borderRadius: 4,
                              border: "1px solid #eee",
                            }}
                          />
                        );
                      } else if (isVideo) {
                        return (
                          <video
                            key={i}
                            src={fullUrl}
                            style={{
                              maxWidth: 40,
                              maxHeight: 30,
                              borderRadius: 4,
                              border: "1px solid #eee",
                            }}
                            controls
                          />
                        );
                      } else {
                        return (
                          <a
                            key={i}
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Lihat File
                          </a>
                        );
                      }
                    })}
                    {files.length > 2 && (
                      <span
                        style={{ fontSize: 12, color: "#888", marginLeft: 4 }}
                      >
                        +{files.length - 2} lainnya
                      </span>
                    )}
                  </div>
                );
              } catch (e) {
                dokumentasiCell = "-";
              }
            }
            // Kolom verifikasi
            let verifikasiCell = "-";
            if (
              laporan &&
              typeof laporan.verifikasi !== "undefined" &&
              laporan.verifikasi !== null
            ) {
              if (laporan.verifikasi === 1 || laporan.verifikasi === "1") {
                verifikasiCell = (
                  <img
                    src={checked}
                    alt="checked"
                    style={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                );
              } else {
                verifikasiCell = (
                  <img
                    src={unchecked}
                    alt="unchecked"
                    style={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                );
              }
            } else {
              verifikasiCell = (
                <img
                  src={unchecked}
                  alt="unchecked"
                  style={{ width: 24, height: 24, objectFit: "contain" }}
                />
              );
            }
            return {
              uraian_pekerjaan: (
                <span style={{ marginLeft: 24 }}>{sub.uraian_pekerjaan}</span>
              ),
              volume: sub.volume,
              satuan: sub.satuan,
              harga_satuan: formatRupiah(hargaSatuan.toString()),
              total_harga: formatRupiah(totalHarga.toString()),
              bobot,
              realisasi: laporan ? laporan.progres_realisasi : "-",
              tenaga_kerja: laporan ? laporan.jumlah_tenaga_kerja : "-",
              catatan: catatanCell,
              dokumentasi: dokumentasiCell,
              verifikasi: verifikasiCell,
              aksi:
                laporan && laporan.progres_realisasi ? null : (
                  <MDButton
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={() => handleOpenInputModal(sub, idx)}
                  >
                    Input Progres
                  </MDButton>
                ),
              isUtama: false,
            };
          });
          const totalHargaUtama = subs.reduce(
            (sum, s) =>
              sum + (Number(s.total_harga.replace(/[^\d]/g, "")) || 0),
            0
          );
          const bobotUtama =
            totalAll > 0
              ? ((totalHargaUtama / totalAll) * 100).toFixed(2)
              : "0.00";
          // Baris judul pekerjaan utama (dengan total & bobot di kolom yang sama)
          rows.push({
            uraian_pekerjaan: <b>{utama.nama_pekerjaan}</b>,
            volume: "",
            satuan: "",
            harga_satuan: "",
            total_harga: <b>{formatRupiah(totalHargaUtama.toString())}</b>,
            bobot: <b>{bobotUtama}</b>,
            realisasi: "",
            tenaga_kerja: "",
            catatan: "",
            dokumentasi: "",
            verifikasi: "",
            aksi: "",
            isUtama: true,
          });
          rows.push(...subs);
        });
        setTableRows(rows);
      })
      .catch(() => setTableRows([]));
  };

  useEffect(() => {
    if (selectedProyek && selectedProyek.value && selectedDate) {
      fetchPekerjaanProyek(selectedProyek.value);
    } else {
      setTableRows([]);
    }
  }, [selectedProyek, selectedDate]);

  const handleOpenInputModal = (sub, idx) => {
    setInputData({
      realisasi: "",
      tenaga_kerja: "",
      catatan: "",
      dokumentasi: [],
      satuan: sub.satuan,
      idx: idx,
      nama_sub_pekerjaan: sub.uraian_pekerjaan,
      id_sub_pekerjaan: sub.id_sub_pekerjaan,
    });

    // Buka form langsung tanpa peringatan lokasi
    setOpenInputModal(true);
  };

  // Fungsi untuk menutup dialog warning lokasi
  const handleCloseLocationWarningDialog = () => {
    setShowLocationWarningDialog(false);
  };

  // Nonaktifkan jalur lanjut input ketika tidak di lokasi proyek
  const handleContinueInput = () => {
    setShowLocationWarningDialog(false);
  };

  const handleSaveInputModal = async () => {
    try {
      setSaveLoading(true);
      const formData = new FormData();
      const today = new Date(selectedDate);
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const todayStr = `${yyyy}-${mm}-${dd}`;
      formData.append("tanggal", todayStr); // atau ambil dari input jika ada
      formData.append("id_sub_pekerjaan", inputData.id_sub_pekerjaan);
      formData.append("id_user", idUser);
      formData.append("progres_realisasi", inputData.realisasi);
      formData.append("jumlah_tenaga_kerja", inputData.tenaga_kerja);
      formData.append("catatan", inputData.catatan || "");

      // Tambahkan koordinat lokasi perangkat jika tersedia
      if (currentLocation.latitude && currentLocation.longitude) {
        formData.append("latitude_perangkat", currentLocation.latitude);
        formData.append("longitude_perangkat", currentLocation.longitude);
        formData.append("accuracy_perangkat", currentLocation.accuracy);
        formData.append(
          "timestamp_lokasi",
          currentLocation.timestamp.toISOString()
        );
        if (currentLocation.address) {
          formData.append("alamat_perangkat", currentLocation.address);
        }
        // Tambahkan jarak dari proyek jika tersedia
        if (distanceFromProject !== null) {
          formData.append("jarak_dari_proyek", distanceFromProject);
        }
        // Tambahkan status lokasi proyek
        formData.append(
          "berada_di_lokasi_proyek",
          isAtProjectLocation ? "1" : "0"
        );
      }

      if (inputData.dokumentasi && Array.isArray(inputData.dokumentasi)) {
        inputData.dokumentasi.forEach((file) => {
          formData.append("dokumentasi_foto", file);
        });
      }
      await axios.post(`${API_HOHOLOK}laporan-harian`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOpenInputModal(false);
      swal("Berhasil", "Laporan harian berhasil dikirim!", "success");
      fetchPekerjaanProyek(selectedProyek.value);
      // TODO: refresh data atau tampilkan notifikasi sukses
    } catch (err) {
      swal(
        "Gagal",
        err.response?.data?.error || err.message || "Terjadi kesalahan",
        "error"
      );
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          <MDBox mb={2} display="flex" alignItems="center" gap={2}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={proyekList}
                      value={selectedProyek}
                      onChange={(event, newValue) => {
                        setSelectedProyek(newValue);
                        // Hitung jarak dari proyek baru jika lokasi sudah tersedia
                        if (
                          newValue &&
                          currentLocation.latitude &&
                          currentLocation.longitude
                        ) {
                          setTimeout(() => {
                            calculateDistanceFromProject();
                          }, 100);
                        }
                      }}
                      loading={loading}
                      getOptionLabel={(option) => option.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Pilih Proyek"
                          variant="standard"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                      sx={{ minWidth: 250 }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    <MDDatePicker
                      value={selectedDate}
                      onChange={handleDateChange}
                      input={{ placeholder: "Select a date" }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </MDBox>

          {selectedProyek && (
            <MDBox
              mt={1}
              p={3}
              borderRadius={2}
              bgcolor="#f0f4fa"
              boxShadow={2}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    color="info.main"
                    mb={1}
                  >
                    {selectedProyek.label}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>Lokasi:</b> {selectedProyek.lokasi || "-"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>Desa:</b> {selectedProyek.desa || "-"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>Koordinat:</b>{" "}
                    {selectedProyek.latitude && selectedProyek.longitude
                      ? `${selectedProyek.latitude}, ${selectedProyek.longitude}`
                      : "-"}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="body2" color="text">
                    <b>Tanggal Mulai:</b>{" "}
                    {formatTanggalIndo(selectedProyek.tanggal_mulai)}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>Tanggal Selesai:</b>{" "}
                    {formatTanggalIndo(selectedProyek.tanggal_selesai)}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>Konsultan Perencana:</b>{" "}
                    {selectedProyek.nama_konsultan_perencana ||
                      selectedProyek.konsultan_perencana?.nama ||
                      "-"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>Kontraktor Pelaksana:</b>{" "}
                    {selectedProyek.nama_kontraktor_pelaksana ||
                      selectedProyek.kontraktor_pelaksana?.nama ||
                      "-"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>Konsultan Pengawas:</b>{" "}
                    {selectedProyek.nama_konsultan_pengawas ||
                      selectedProyek.konsultan_pengawas?.nama ||
                      "-"}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>
          )}
          {/* Informasi Lokasi Perangkat */}
          <MDBox mt={2} p={3} borderRadius={2} bgcolor="#f8f9fa" boxShadow={1}>
            <MDTypography
              variant="h6"
              fontWeight="bold"
              color="info.main"
              mb={2}
            >
              <LocationOnIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Lokasi Perangkat Saat Ini
            </MDTypography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                {locationLoading ? (
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={20} />
                    <MDTypography variant="body2" color="text.secondary">
                      Mendapatkan lokasi...
                    </MDTypography>
                  </MDBox>
                ) : locationError ? (
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <CancelIcon color="error" />
                    <MDTypography variant="body2" color="error">
                      {locationError}
                    </MDTypography>
                  </MDBox>
                ) : currentLocation.latitude && currentLocation.longitude ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="body2" color="text">
                        <b>Koordinat:</b> {currentLocation.latitude.toFixed(6)},{" "}
                        {currentLocation.longitude.toFixed(6)}
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        <b>Akurasi:</b> ±{Math.round(currentLocation.accuracy)}{" "}
                        meter
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        <b>Waktu:</b>{" "}
                        {currentLocation.timestamp.toLocaleString("id-ID")}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {currentLocation.address && (
                        <MDTypography variant="body2" color="text">
                          <b>Alamat:</b> {currentLocation.address}
                        </MDTypography>
                      )}
                      {typeof distanceFromProject === "number" &&
                        selectedProyek && (
                          <MDTypography
                            variant="body2"
                            color={
                              distanceFromProject <= projectRadiusKm
                                ? "success.main"
                                : distanceFromProject <= projectRadiusKm * 4
                                ? "warning.main"
                                : "error.main"
                            }
                            sx={{ fontWeight: "bold" }}
                          >
                            <StraightenIcon
                              sx={{
                                mr: 0.5,
                                verticalAlign: "middle",
                                fontSize: 16,
                              }}
                            />
                            <b>Jarak dari Proyek:</b>{" "}
                            {distanceFromProject
                              ? `${distanceFromProject.toFixed(2)} km`
                              : "Menghitung jarak..."}
                            {proximityLabel}
                          </MDTypography>
                        )}
                      {/* Status Lokasi Proyek */}
                      {distanceFromProject !== null && (
                        <MDBox
                          mt={1}
                          p={1}
                          borderRadius={1}
                          bgcolor={isAtProjectLocation ? "#e8f5e9" : "#ffebee"}
                        >
                          <MDTypography
                            variant="body2"
                            color={
                              isAtProjectLocation
                                ? "success.main"
                                : "error.main"
                            }
                            sx={{
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {isAtProjectLocation ? (
                              <>
                                <CheckCircleIcon
                                  sx={{ mr: 0.5, fontSize: 16 }}
                                />
                                ✓ Berada di lokasi proyek (≤{" "}
                                {selectedProyek?.radiusInput || 250}m)
                              </>
                            ) : (
                              <>
                                <CancelIcon sx={{ mr: 0.5, fontSize: 16 }} />✗
                                Tidak berada di lokasi proyek (&gt;{" "}
                                {selectedProyek?.radiusInput || 250}m)
                              </>
                            )}
                          </MDTypography>
                        </MDBox>
                      )}
                      <MDBox mt={1}>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={copyCoordinatesToClipboard}
                        >
                          Salin Koordinat
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                ) : (
                  <MDTypography variant="body2" color="text.secondary">
                    Lokasi belum tersedia
                  </MDTypography>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <MDButton
                  variant="contained"
                  color="info"
                  onClick={getCurrentLocation}
                  disabled={locationLoading}
                  startIcon={
                    locationLoading ? <CircularProgress size={16} /> : null
                  }
                >
                  {locationLoading ? "Mendapatkan Lokasi..." : "Refresh Lokasi"}
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
          <Card>
            <MDBox p={3} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Laporan Harian Proyek
              </MDTypography>
              <MDTypography variant="button" color="text">
                {selectedProyek?.label}
              </MDTypography>
            </MDBox>
            <DataTable
              table={{ columns: tableColumns, rows: tableRows }}
              entriesPerPage={{ defaultValue: 100, values: [100] }}
            />
          </Card>
          <Dialog
            open={openInputModal}
            onClose={() => setOpenInputModal(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Input Realisasi Sub Pekerjaan {inputData.nama_sub_pekerjaan}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Realisasi"
                    name="realisasi"
                    value={inputData.realisasi}
                    onChange={(e) =>
                      setInputData({ ...inputData, realisasi: e.target.value })
                    }
                    type="number"
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: <span>{inputData.satuan}</span>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Jumlah Tenaga Kerja"
                    name="tenaga_kerja"
                    value={inputData.tenaga_kerja}
                    onChange={(e) =>
                      setInputData({
                        ...inputData,
                        tenaga_kerja: e.target.value,
                      })
                    }
                    type="number"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Catatan"
                    name="catatan"
                    value={inputData.catatan}
                    onChange={(e) =>
                      setInputData({ ...inputData, catatan: e.target.value })
                    }
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDBox p={2} bgcolor="#f8f9fa" borderRadius={1}>
                    <MDTypography variant="body2" color="text" mb={1}>
                      <b>Informasi Lokasi Perangkat:</b>
                    </MDTypography>
                    {locationLoading ? (
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <MDTypography variant="caption" color="text.secondary">
                          Mendapatkan lokasi...
                        </MDTypography>
                      </MDBox>
                    ) : locationError ? (
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <CancelIcon color="error" sx={{ fontSize: 16 }} />
                        <MDTypography variant="caption" color="error">
                          {locationError}
                        </MDTypography>
                      </MDBox>
                    ) : currentLocation.latitude &&
                      currentLocation.longitude ? (
                      <MDBox>
                        <MDTypography variant="caption" color="text.secondary">
                          Koordinat: {currentLocation.latitude.toFixed(6)},{" "}
                          {currentLocation.longitude.toFixed(6)}
                        </MDTypography>
                        <br />
                        <MDTypography variant="caption" color="text.secondary">
                          Akurasi: ±{Math.round(currentLocation.accuracy)} meter
                        </MDTypography>
                        {currentLocation.address && (
                          <>
                            <br />
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Alamat: {currentLocation.address}
                            </MDTypography>
                          </>
                        )}
                        {distanceFromProject !== null && selectedProyek && (
                          <>
                            <br />
                            <MDTypography
                              variant="caption"
                              color={
                                distanceFromProject &&
                                distanceFromProject <= 0.5
                                  ? "success.main"
                                  : distanceFromProject &&
                                    distanceFromProject <= 2
                                  ? "warning.main"
                                  : "error.main"
                              }
                              sx={{ fontWeight: "bold" }}
                            >
                              <StraightenIcon
                                sx={{
                                  mr: 0.5,
                                  verticalAlign: "middle",
                                  fontSize: 12,
                                }}
                              />
                              Jarak dari Proyek:{" "}
                              {distanceFromProject
                                ? `${distanceFromProject.toFixed(2)} km`
                                : "Menghitung jarak..."}
                              {proximityLabel}
                            </MDTypography>
                          </>
                        )}
                        {/* Status Lokasi di Modal Input */}
                        {distanceFromProject !== null && (
                          <>
                            <br />
                            <MDTypography
                              variant="caption"
                              color={
                                isAtProjectLocation
                                  ? "success.main"
                                  : "error.main"
                              }
                              sx={{ fontWeight: "bold" }}
                            >
                              {isAtProjectLocation ? (
                                <>
                                  <CheckCircleIcon
                                    sx={{
                                      mr: 0.5,
                                      verticalAlign: "middle",
                                      fontSize: 12,
                                    }}
                                  />
                                  ✓ Berada di lokasi proyek
                                </>
                              ) : (
                                <>
                                  <CancelIcon
                                    sx={{
                                      mr: 0.5,
                                      verticalAlign: "middle",
                                      fontSize: 12,
                                    }}
                                  />
                                  ✗ Tidak berada di lokasi proyek
                                </>
                              )}
                            </MDTypography>
                          </>
                        )}
                      </MDBox>
                    ) : (
                      <MDTypography variant="caption" color="text.secondary">
                        Lokasi belum tersedia
                      </MDTypography>
                    )}
                  </MDBox>
                </Grid>
                <Grid item xs={12}>
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    Upload Dokumentasi (Foto/Video)
                  </MDTypography>
                  {renderPreview()}
                  {useMemo(
                    () => (
                      <MDDropzone
                        options={{
                          addRemoveLinks: true,
                          uploadMultiple: true,
                          acceptedFiles: "image/*,video/*",
                        }}
                        onChange={handleDropzoneChange}
                        ref={dropzoneRef}
                      />
                    ),
                    []
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenInputModal(false)}>Batal</Button>
              <MDButton
                onClick={handleSaveInputModal}
                variant="contained"
                color="info"
                disabled={
                  typeof distanceFromProject === "number"
                    ? distanceFromProject > projectRadiusKm
                    : !isAtProjectLocation || saveLoading
                }
                title={
                  typeof distanceFromProject === "number" &&
                  distanceFromProject > projectRadiusKm
                    ? `Di luar radius proyek (> ${(
                        projectRadiusKm * 1000
                      ).toFixed(0)} m)`
                    : undefined
                }
              >
                {saveLoading ? (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} color="inherit" />
                    <span>Mengirim...</span>
                  </MDBox>
                ) : (
                  "Simpan"
                )}
              </MDButton>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openPreviewDialog}
            onClose={() => setOpenPreviewDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Preview Dokumentasi</DialogTitle>
            <DialogContent dividers>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                {previewFiles.map((url, i) => {
                  const pfFullUrl = url.startsWith("http")
                    ? url
                    : `${HOHOLOK_UPLOADS}${url}`;
                  const pfIsImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(
                    pfFullUrl
                  );
                  const pfIsVideo = /\.(mp4|webm|ogg|mov)$/i.test(pfFullUrl);
                  if (pfIsImage) {
                    return (
                      <a
                        key={i}
                        href={pfFullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "inline-block" }}
                      >
                        <img
                          src={pfFullUrl}
                          alt="dokumentasi"
                          style={{
                            maxWidth: 200,
                            maxHeight: 140,
                            borderRadius: 6,
                            border: "1px solid #eee",
                            marginBottom: 8,
                            cursor: "pointer",
                          }}
                        />
                      </a>
                    );
                  } else if (pfIsVideo) {
                    return (
                      <a
                        key={i}
                        href={pfFullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "inline-block" }}
                      >
                        <video
                          src={pfFullUrl}
                          style={{
                            maxWidth: 200,
                            maxHeight: 140,
                            borderRadius: 6,
                            border: "1px solid #eee",
                            marginBottom: 8,
                            cursor: "pointer",
                          }}
                          controls
                        />
                      </a>
                    );
                  } else {
                    return (
                      <a
                        key={i}
                        href={pfFullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Lihat File
                      </a>
                    );
                  }
                })}
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenPreviewDialog(false)}
                color="primary"
              >
                Tutup
              </Button>
            </DialogActions>
          </Dialog>
          {/* Dialog Warning Lokasi */}
          {/* Dialog Warning Lokasi di-nonaktifkan sesuai alur baru */}
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Verifikasi_Progres;
